"use client";

import { useCallback } from "react";
import { useNotificationContext } from "./NotificationContext";
import type { NotificationConfig } from "./types";

/**
 * 使用通知的Hook
 * 提供便捷的通知操作方法
 *
 * @example
 * ```tsx
 * const { notify, success, error, warning, info } = useNotification();
 *
 * // 基本用法
 * notify({
 *   title: "Macro Alert",
 *   message: "AMD CEO Lisa Su just bought $2.1M of stock",
 *   important: true,
 *   time: "Today 8:00AM"
 * });
 *
 * // 带跳转链接
 * success({
 *   title: "Success",
 *   message: "Operation completed",
 *   link: "/dashboard"
 * });
 *
 * // 自定义配置
 * notify({
 *   title: "Custom",
 *   message: "Custom notification",
 *   autoClose: false, // 不自动关闭
 *   duration: 5000, // 5秒后关闭
 *   onClick: () => console.log("Clicked")
 * });
 * ```
 */
export const useNotification = () => {
  const { showNotification, closeNotification } = useNotificationContext();

  /**
   * 显示通知
   */
  const notify = useCallback(
    (config: NotificationConfig) => {
      showNotification(config);
    },
    [showNotification],
  );

  /**
   * 显示成功通知
   */
  const success = useCallback(
    (config: Omit<NotificationConfig, "type">) => {
      showNotification({ ...config, type: "success" });
    },
    [showNotification],
  );

  /**
   * 显示错误通知
   */
  const error = useCallback(
    (config: Omit<NotificationConfig, "type">) => {
      showNotification({ ...config, type: "error" });
    },
    [showNotification],
  );

  /**
   * 显示警告通知
   */
  const warning = useCallback(
    (config: Omit<NotificationConfig, "type">) => {
      showNotification({ ...config, type: "warning" });
    },
    [showNotification],
  );

  /**
   * 显示信息通知
   */
  const info = useCallback(
    (config: Omit<NotificationConfig, "type">) => {
      showNotification({ ...config, type: "info" });
    },
    [showNotification],
  );

  /**
   * 关闭当前通知
   */
  const close = useCallback(() => {
    closeNotification();
  }, [closeNotification]);

  return {
    notify,
    success,
    error,
    warning,
    info,
    close,
  };
};
